import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogTitle, 
  TextField, 
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box, 
  Typography, 
  CircularProgress,
  Button,
  InputAdornment,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd'; // ✅ YENİ: Yeni cari ikonu
import accountingService, { AccountDto } from '../../../../services/accountingService';
import { useTranslation } from 'react-i18next';

// QuickSaleScreen'den gelen Customer tipini import et
import { Customer as QuickSaleCustomer } from '../QuickSaleScreen';

// Modal için genişletilmiş Customer interface
interface ExtendedCustomer extends QuickSaleCustomer {
  taxNumber?: string;
  taxOffice?: string;
  address?: string;
}

const PAGE_SIZE = 20; // Her seferinde 20 cari yükle

interface CustomerSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelectCustomer: (customer: QuickSaleCustomer) => void;
  companyId: string | null;
  onOpenAddAccount?: () => void; // ✅ YENİ: Yeni cari ekleme modalını açma
}

const CustomerSelectionModal: React.FC<CustomerSelectionModalProps> = ({
  open,
  onClose,
  onSelectCustomer,
  companyId,
  onOpenAddAccount // ✅ YENİ
}) => {
  const { t } = useTranslation('salesSystem');
  const [allCustomers, setAllCustomers] = useState<ExtendedCustomer[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Component yüklendiğinde ve her modalın açılışında carileri yükle
  useEffect(() => {
    if (open && companyId) {
      // Modal açıldığında state'i sıfırla
      setAllCustomers([]);
      setCurrentPage(1);
      setSearchTerm('');
      setHasMore(false);
      setTotalCount(0);
      loadCustomers(1, '');
    }
  }, [open, companyId]);

  // Modal açıldığında input'a fokusla
  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 300);
    }
  }, [open]);

  // Carileri SQL'den yükle (sayfalama + arama)
  const loadCustomers = useCallback(async (page: number, search: string) => {
    if (!companyId) {
      setErrorMessage("Şirket ID bulunamadı");
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    
    try {
      console.log(`[CustomerSelectionModal] 🔄 SQL'den ${page}. sayfa carileri çekiliyor (Arama: "${search}")`);
      
      const response = await accountingService.getAccountsPaged(page, PAGE_SIZE, search);
      
      const mappedCustomers: ExtendedCustomer[] = response.items.map((acc: AccountDto) => {
        // Backend camelCase dönüyor, ikisini de kontrol et
        const rawAcc = acc as any;
        return {
          id: (acc.Id || rawAcc.id)?.toString() || '',
          companyName: acc.CompanyName || rawAcc.companyName || 'İsimsiz Cari',
          balance: acc.CreditBalance || rawAcc.creditBalance || undefined,
          debitBalance: acc.DebitBalance || rawAcc.debitBalance || 0,
          creditBalance: acc.CreditBalance || rawAcc.creditBalance || 0,
          mobilePhone: acc.MobilePhone || rawAcc.mobilePhone || '',
          email: acc.Email || rawAcc.email || '',
          isActive: (acc.IsActive !== false) && (rawAcc.isActive !== false),
          taxNumber: acc.TaxNumber || rawAcc.taxNumber || undefined,
          taxOffice: acc.TaxOffice || rawAcc.taxOffice || undefined,
          address: acc.Address || rawAcc.address || undefined,
        };
      });

      // Sayfa 1 ise listeyi değiştir, değilse ekle (scroll pagination)
      if (page === 1) {
        setAllCustomers(mappedCustomers);
      } else {
        setAllCustomers(prev => [...prev, ...mappedCustomers]);
      }

      setTotalCount(response.totalCount);
      setHasMore(response.hasMore);
      setCurrentPage(page);

      console.log(`[CustomerSelectionModal] ✅ ${mappedCustomers.length} cari yüklendi (Toplam: ${response.totalCount}, Daha var: ${response.hasMore})`);
      
    } catch (error) {
      console.error('[CustomerSelectionModal] ❌ Cariler yüklenirken hata:', error);
      setErrorMessage("Cari hesaplar yüklenirken bir hata oluştu");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // Arama terimi değiştiğinde
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    
    // Debounce için timeout
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      loadCustomers(1, newSearchTerm);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [loadCustomers]);

  // Scroll pagination
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const bottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;

    if (bottom && !loading && hasMore) {
      console.log('[CustomerSelectionModal] 📜 Scroll pagination - sonraki sayfa yükleniyor...');
      loadCustomers(currentPage + 1, searchTerm);
    }
  }, [loading, hasMore, currentPage, searchTerm, loadCustomers]);

  // Müşteri seçildiğinde
  const handleCustomerSelection = (customer: ExtendedCustomer) => {
    console.log('[CustomerSelectionModal] 🎯 Cari seçildi:', customer.companyName);
    // QuickSaleCustomer tipine uyumlu hale getir (fazla property'ler otomatik ignore edilir)
    onSelectCustomer(customer);
    onClose();
  };

  // Para birimi formatlama
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Bakiye hesapla
  const calculateBalance = (customer: ExtendedCustomer): number => {
    const debitBalance = customer.debitBalance || 0; 
    const creditBalance = customer.creditBalance !== undefined ? customer.creditBalance : (customer.balance || 0);
    return creditBalance - debitBalance;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: { borderRadius: 2, height: '70vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1 }} />
            {t('customerSelectionModal.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalCount > 0 ? t('customerSelectionModal.totalAccounts', { count: totalCount }) : ''}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
        {/* Arama kutusu */}
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            fullWidth
            placeholder={t('customerSelectionModal.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            inputRef={searchInputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
          />
        </Box>

        <Divider />

        {/* Hata mesajı */}
        {errorMessage && (
          <Box sx={{ p: 2 }}>
            <Typography color="error" variant="body2">
              {errorMessage}
            </Typography>
          </Box>
        )}

        {/* Cari listesi */}
        <Box
          ref={listRef}
          onScroll={handleScroll}
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            maxHeight: 'calc(70vh - 180px)',
          }}
        >
          {loading && allCustomers.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
            </Box>
          ) : allCustomers.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {searchTerm ? t('customerSelectionModal.empty.search') : t('customerSelectionModal.empty.default')}
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {allCustomers.map((customer) => (
                <React.Fragment key={customer.id}>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleCustomerSelection(customer)}>
                      <ListItemText
                        primary={customer.companyName}
                        secondary={
                          <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {customer.taxNumber && (
                              <Typography variant="caption" component="span" color="text.secondary">
                                VKN: {customer.taxNumber}
                              </Typography>
                            )}
                            {customer.mobilePhone && (
                              <Typography variant="caption" component="span" color="text.secondary">
                                Tel: {customer.mobilePhone}
                              </Typography>
                            )}
                            <Typography 
                              variant="caption" 
                              component="span" 
                              sx={{
                                fontWeight: 'bold',
                                color: calculateBalance(customer) >= 0 ? 'success.main' : 'error.main',
                              }}
                            >
                              {t('customerSelectionModal.balance')}: {formatCurrency(calculateBalance(customer))}
                            </Typography>
                          </Box>
                        }
                        primaryTypographyProps={{
                          fontWeight: 500,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              
              {/* Scroll pagination loading */}
              {loading && allCustomers.length > 0 && (
                <ListItem>
                  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                </ListItem>
              )}
              
              {/* Daha fazla yok mesajı */}
              {!hasMore && allCustomers.length > 0 && (
                <ListItem>
                  <Typography variant="caption" color="text.secondary" sx={{ width: '100%', textAlign: 'center', py: 1 }}>
                    {t('customerSelectionModal.allLoaded')}
                  </Typography>
                </ListItem>
              )}
            </List>
          )}
        </Box>

        {/* Alt butonlar */}
        <Box sx={{ p: 2, pt: 1, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1 }}>
          {/* ✅ YENİ: Yeni Cari Kayıt Butonu */}
          {onOpenAddAccount && (
            <Button 
              fullWidth 
              variant="contained" 
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={() => {
                onOpenAddAccount();
                onClose(); // ✅ Modalı kapat
              }}
            >
              {t('customerSelectionModal.newAccount')}
            </Button>
          )}
          <Button 
            fullWidth 
            variant="outlined" 
            onClick={onClose}
          >
            {t('customerSelectionModal.cancel')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerSelectionModal;
